import { Link } from "react-router-dom";
import { Car, Bike, Check, ArrowRight, PoundSterling, UserCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { PATHWAYS } from "../lib/content";
import { useContent } from "../context/ContentContext";
import HeroBackdrop from "../components/HeroBackdrop";
import { CallbackForm } from "../components/CallbackForm";

const DRIVER_COCKPIT = [
  { k: "D", label: "Doors", text: "All doors closed and secure before moving off." },
  { k: "S", label: "Seat", text: "Seat, backrest and head restraint adjusted so you can reach the pedals and controls comfortably." },
  { k: "S", label: "Steering", text: "Set reach and height so your hands rest on the wheel with a slight bend at the elbow." },
  { k: "S", label: "Seatbelt", text: "Belt on, correctly routed and not twisted — and check your passengers are belted too." },
  { k: "M", label: "Mirrors", text: "Interior and both door mirrors adjusted for the best all-round view." },
];

export default function Pathway({ kind }) {
  const { t } = useContent();
  const p = PATHWAYS[kind];
  const ck = kind === "rider" ? "riders" : "drivers";
  const Icon = kind === "driver" ? Car : Bike;

  return (
    <div data-testid={`pathway-${kind}-page`}>
      {/* Hero */}
      <section className="relative bg-[#0F2744] overflow-hidden">
        <HeroBackdrop sectionKey={ck} image={t(`img.${ck}`, p.hero)} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold" style={{ background: p.accent, color: kind === "rider" ? "#0F2744" : "#fff" }}>
            <Icon className="h-4 w-4" /> {p.label}
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight max-w-3xl">{t(`${ck}.title`, p.title)}</h1>
          <p className="mt-5 text-lg text-slate-200 max-w-2xl">{t(`${ck}.intro`, p.intro)}</p>
          <Button asChild size="lg" className="mt-8 bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold px-8">
            <Link to="/join" data-testid={`${kind}-join-btn`}>{t(`${ck}.cta`, "Start your application")} <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </Button>
        </div>
      </section>

      {/* Why */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <span className="eyebrow">Why take advanced training?</span>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0F2744] max-w-2xl">The benefits go far beyond passing a test</h2>
        <div className="mt-10 grid sm:grid-cols-2 gap-6">
          {p.why.map((w, i) => (
            <div key={i} className="rounded-2xl bg-white border border-slate-200 p-6" data-testid={`why-${i}`}>
              <div className="h-10 w-10 rounded-lg flex items-center justify-center mb-4" style={{ background: p.bgLight }}>
                <Check className="h-6 w-6" style={{ color: p.accent }} />
              </div>
              <h3 className="text-lg font-bold text-[#0F2744]">{w.title}</h3>
              <p className="mt-2 text-slate-600">{w.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section style={{ background: p.bgLight }} className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="eyebrow">How training works</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0F2744]">Your pathway, step by step</h2>
          <div className="mt-10 grid md:grid-cols-4 gap-6">
            {p.steps.map((s) => (
              <div key={s.n} className="relative rounded-2xl bg-white border border-slate-200 p-6" data-testid={`step-${s.n}`}>
                <div className="h-11 w-11 rounded-full flex items-center justify-center font-heading font-bold text-white mb-4" style={{ background: p.accent }}>
                  {s.n}
                </div>
                <h3 className="font-bold text-[#0F2744]">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {kind === "driver" && (
        <section className="bg-white border-t border-slate-100 py-16" data-testid="driver-training-teaser">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="eyebrow">Meet the people</span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0F2744]">Our dedicated driver Training Team</h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Our team is made up entirely of volunteers — led by an experienced Head of Training, and every tutor
                holds the RoSPA Advanced Tutor qualification. They deliver observed drives, one-to-one coaching,
                hazard &amp; commentary drives and continuous development.
              </p>
              <Button asChild size="lg" className="mt-6 font-bold px-8" style={{ background: p.accent, color: "#0F2744" }}>
                <Link to="/drivers/training" data-testid="driver-training-link">Meet the Training Team <ArrowRight className="h-4 w-4 ml-2" /></Link>
              </Button>
            </div>
          </div>
        </section>
      )}
      {kind === "rider" && (
        <section className="bg-white border-t border-slate-100 py-16" data-testid="rider-training-teaser">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
            <div className="grid grid-cols-2 gap-3 order-2 lg:order-1">
              <img src={t("img.pathway.teaser1", "https://customer-assets-39nsmqrw.emergentagent.net/job_rospa-wm-portal/artifacts/17pxmkkt_above-the-clouds-Medium.jpeg")} alt="Rider on a scenic moorland road" className="rounded-2xl object-cover h-56 w-full col-span-2" />
              <img src={t("img.pathway.teaser2", "https://customer-assets-39nsmqrw.emergentagent.net/job_rospa-wm-portal/artifacts/gu08wcps_skills-day-Medium.jpg")} alt="Slow control practice" className="rounded-2xl object-cover h-40 w-full" />
              <img src={t("img.pathway.teaser3", "https://customer-assets-39nsmqrw.emergentagent.net/job_rospa-wm-portal/artifacts/jyt8bv4m_Skills-day-watching-closely-Medium.jpg")} alt="Tutor watching an associate closely" className="rounded-2xl object-cover h-40 w-full" />
            </div>
            <div className="order-1 lg:order-2">
              <span className="eyebrow">Meet the people</span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0F2744]">Our dedicated Training Team</h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Our team is made up entirely of volunteers — led by an experienced Head of Training, with every tutor a
                qualified RoSPA Advanced Tutor. They deliver observed rides, one-to-one coaching, slow-riding and
                machine-control days, and more.
              </p>
              <Button asChild size="lg" className="mt-6 font-bold px-8" style={{ background: p.accent, color: "#0F2744" }}>
                <Link to="/riders/training" data-testid="rider-training-link">Meet the Training Team <ArrowRight className="h-4 w-4 ml-2" /></Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Costs & eligibility */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-8">
        <div className="rounded-2xl bg-white border border-slate-200 p-8" data-testid="costs-card">
          <div className="h-11 w-11 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
            <PoundSterling className="h-6 w-6 text-amber-600" />
          </div>
          <h3 className="text-xl font-bold text-[#0F2744]">Costs</h3>
          <ul className="mt-4 space-y-2.5 text-slate-700 text-sm" data-testid="costs-list">
            <li className="flex justify-between gap-3 border-b border-slate-100 pb-2.5"><span>Joining fee{kind === "rider" ? " (includes Roadcraft & Highway Code)" : ""}</span><span className="font-bold text-[#0F2744] whitespace-nowrap">{t("fee.join", "£65")}</span></li>
            <li className="flex justify-between gap-3 border-b border-slate-100 pb-2.5"><span>Annual club subscription (each April)</span><span className="font-bold text-[#0F2744] whitespace-nowrap">{t("fee.renewal", "£20")}</span></li>
            <li className="flex justify-between gap-3 border-b border-slate-100 pb-2.5"><span>Fuel contribution to your tutor, per session</span><span className="font-bold text-[#0F2744] whitespace-nowrap">~£20</span></li>
            <li className="flex justify-between gap-3"><span>After you pass: yearly RoSPA head-office fee, plus your annual club subscription</span><span className="font-bold text-[#0F2744] whitespace-nowrap">RoSPA rate</span></li>
          </ul>
          <p className="mt-4 text-sm text-slate-500 leading-relaxed">{t(`${ck}.costs`, p.costs)}</p>
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-8" data-testid="eligibility-card">
          <div className="h-11 w-11 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
            <UserCheck className="h-6 w-6 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-[#0F2744]">Eligibility</h3>
          <p className="mt-3 text-slate-600 leading-relaxed">{t(`${ck}.eligibility`, p.eligibility)}</p>
        </div>
      </section>

      {kind === "driver" && (
        <section className="bg-white border-t border-slate-100 py-20" data-testid="driver-checks">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="eyebrow">Before you drive</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0F2744]">Driver checks & the cockpit drill</h2>
            <p className="mt-4 text-slate-600 max-w-2xl">Every drive should start with a quick, methodical routine. The cockpit drill — <strong>DSSSM</strong> — sets you up for safety and full control before you move off.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {DRIVER_COCKPIT.map((it, i) => (
                <div key={i} className="flex items-start gap-4 rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid={`cockpit-${i}`}>
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center font-heading font-bold text-white shrink-0 bg-blue-600">{it.k}</div>
                  <div>
                    <div className="font-bold text-[#0F2744]">{it.label}</div>
                    <p className="text-sm text-slate-600 mt-0.5 leading-relaxed">{it.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-2xl bg-blue-50 border border-blue-100 p-6">
              <h3 className="font-bold text-[#0F2744]">Under-bonnet & walk-round — POWER</h3>
              <p className="mt-2 text-sm text-slate-600">A regular POWER check keeps your car roadworthy between services:</p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-5 gap-2 text-sm">
                {[["P", "Petrol / fuel"], ["O", "Oil level"], ["W", "Water / coolant"], ["E", "Electrics & lights"], ["R", "Rubber (tyres)"]].map(([k, l]) => (
                  <div key={k} className="rounded-lg bg-white border border-blue-100 p-3"><span className="font-bold text-blue-600">{k}</span> — {l}</div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Request a callback */}
      <section className="bg-white py-16 border-t border-slate-100" data-testid="callback-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <CallbackForm pathway={kind} accent={p.accent} />
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-slate-100 py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="eyebrow">Frequently asked questions</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0F2744] mb-8">Good questions, honest answers</h2>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {p.faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="rounded-xl bg-white border border-slate-200 px-5">
                <AccordionTrigger className="text-left font-semibold text-[#0F2744] hover:no-underline" data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="bg-[#0F2744] py-16 text-center px-4">
        <h2 className="text-3xl font-bold text-white">{t(`${ck}.ctaTitle`, "Take the first step today")}</h2>
        <Button asChild size="lg" className="mt-6 bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold h-14 px-10">
          <Link to="/join" data-testid={`${kind}-cta-join`}>{t(`${ck}.ctaButton`, "Apply to join")}</Link>
        </Button>
      </section>
    </div>
  );
}
